import React, { useState, useEffect } from 'react';
import Sudoku from '../components/Sudoku';
import GameHeader from '../components/GameHeader';
import '../App.css';
import SettingsModal from '../components/modals/SettingsModal';
import { useLanguage, translations } from '../contexts/LanguageContext';
import victoryImage1 from '../assets/images/victory1.webp';
import victoryImage2 from '../assets/images/victory2.webp';
import victoryImage3 from '../assets/images/victory3.webp';
import useIsMobile from '../hooks/useIsMobile';
import Header from '../components/Header';
import { useNavigate } from 'react-router-dom';

function SudokuGame() {
  const { t, language } = useLanguage();
  const [difficulty, setDifficulty] = useState(1);
  const [lives, setLives] = useState(5);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingDifficulty, setPendingDifficulty] = useState(null);
  const [hasUserInput, setHasUserInput] = useState(false);
  const [showRulesModal, setShowRulesModal] = useState(false);
  const [showRestartModal, setShowRestartModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showGameOverModal, setShowGameOverModal] = useState(false);
  const [showGameClearModal, setShowGameClearModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const isMobile = useIsMobile();
  const [resetBoard, setResetBoard] = useState(false);
  const navigate = useNavigate();
  const [showGameListModal, setShowGameListModal] = useState(false);

  const difficultySettings = {
    1: { cells: 55, lives: 5 },
    2: { cells: 50, lives: 5 },
    3: { cells: 45, lives: 5 },
    4: { cells: 42, lives: 4 },
    5: { cells: 40, lives: 4 },
    6: { cells: 37, lives: 3 },
    7: { cells: 30, lives: 3 },
    8: { cells: 25, lives: 2 },
    9: { cells: 22, lives: 2 }
  };

  const difficultyNames = Object.keys(translations[language].difficultyLevels).reduce((acc, key) => {
    acc[key] = t(`difficultyLevels.${key}`);
    return acc;
  }, {});

  const handleDifficultyChange = (level) => {
    if (hasUserInput) {
      setShowConfirmModal(true);
      setPendingDifficulty(level);
    } else {
      setDifficulty(level);
      setLives(difficultySettings[level].lives);
    }
  };

  const handleLoseLife = () => {
    const newLives = Math.max(0, lives - 1);
    setLives(newLives);
    
    if (newLives === 0) {
      setShowGameOverModal(true);
    }
  };

  const handleGameOver = () => {
    setShowGameOverModal(true);
  };

  const handleGameClear = () => {
    setShowGameClearModal(true);
  };

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setShowShareModal(true);
    } catch (err) {
      alert('링크 복사에 실패했어요. 주소창의 URL을 직접 복사해주세요!');
      console.error('클립보드 복사 실패:', err);
    }
  };

  const handleConfirmDifficulty = (confirmed) => {
    if (confirmed && pendingDifficulty !== null) {
      setDifficulty(pendingDifficulty);
      setLives(difficultySettings[pendingDifficulty].lives);
      setHasUserInput(false);
    }
    setShowConfirmModal(false);
    setPendingDifficulty(null);
  };

  const handleRestart = () => {
    setHasUserInput(false);
    setLives(difficultySettings[difficulty].lives);
    setResetBoard(true);
    setTimeout(() => setResetBoard(false), 0);
  };

  const isEvening = () => {
    const hour = new Date().getHours();
    return hour >= 18 || hour < 6;
  };

  const getVictoryImage = (difficulty) => {
    if (difficulty <= 3) {
      return victoryImage1;
    } else if (difficulty <= 6) {
      return victoryImage2;
    } else {
      return victoryImage3;
    }
  };

  const handleGameList = () => {
    if (hasUserInput) {
      setShowGameListModal(true);
    } else {
      setShowGameListModal(true);
    }
  };

  useEffect(() => {
    const initBanner = () => {
      if (window.PartnersCoupang) {
        try {
          const bottomContainer = document.getElementById('coupang-banner-bottom');
          const screenWidth = window.innerWidth;
          let bannerWidth, bannerHeight;

          if (screenWidth <= 375) {
            bannerWidth = "280";
            bannerHeight = "90";
          } else if (screenWidth <= 500) {
            bannerWidth = "320";
            bannerHeight = "100";
          } else {
            bannerWidth = "500";
            bannerHeight = "120";
          }

          if (bottomContainer) {
            new window.PartnersCoupang.G({
              id: 834471,
              template: "carousel",
              trackingCode: "AF1795036",
              width: bannerWidth,
              height: bannerHeight,
              tsource: "",
              container: bottomContainer
            });
          }
        } catch (error) {
          console.error('쿠팡 배너 초기화 오류:', error);
        }
      }
    };

    let bannerTimeoutId = null;
    if (showConfirmModal || showRulesModal || showRestartModal || showShareModal ||
        showContactModal || showGameOverModal || showGameClearModal || showSettingsModal) {
      bannerTimeoutId = setTimeout(initBanner, 100);
    }

    return () => {
      if (bannerTimeoutId !== null) clearTimeout(bannerTimeoutId);
      const bottomBanner = document.getElementById('coupang-banner-bottom');
      if (bottomBanner) bottomBanner.innerHTML = '';
    };
  }, [showConfirmModal, showRulesModal, showRestartModal, showShareModal,
      showContactModal, showGameOverModal, showGameClearModal, showSettingsModal]);

  return (
    <div className="App">
      <Header 
        title={t('title')}
        onRules={() => setShowRulesModal(true)}
        onGameList={handleGameList}
        onSettings={() => setShowSettingsModal(true)}
        onRestart={() => setShowRestartModal(true)}
      />

      <div className="game-header">
        <GameHeader 
          difficulty={difficulty}
          onDifficultyChange={handleDifficultyChange}
          lives={lives}
          difficultyNames={difficultyNames}
          isMobile={isMobile}
        />
      </div>

      <div className="game-container">
        <Sudoku
          difficulty={difficulty}
          onGameOver={handleGameOver}
          onGameClear={handleGameClear}
          onWrongAnswer={handleLoseLife}
          onUserInput={() => setHasUserInput(true)}
          key={difficulty}
          cellsToShow={difficultySettings[difficulty].cells}
          resetBoard={resetBoard}
        />
      </div>

      {showConfirmModal && (
        <div className="modal-overlay">
          <div className="modal">
            <p>{t('modals.confirm.message')}</p>
            <div className="modal-buttons">
              <button onClick={() => handleConfirmDifficulty(false)}>
                {t('modals.confirm.cancel')}
              </button>
              <button onClick={() => handleConfirmDifficulty(true)}>
                {t('modals.confirm.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showRulesModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>{t('modals.rules.title')}</h2>
            <p>{t('modals.rules.description')}</p>
            <ol>
              <li>{t('modals.rules.rule1')}</li>
              <li>{t('modals.rules.rule2')}</li>
            </ol>
            <div className="modal-buttons">
              <button onClick={() => setShowRulesModal(false)}>
                {t('modals.rules.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showRestartModal && (
        <div className="modal-overlay">
          <div className="modal">
            <p>{t('modals.confirm.message')}</p>
            <div className="modal-buttons">
              <button onClick={() => setShowRestartModal(false)}>
                {t('modals.confirm.cancel')}
              </button>
              <button onClick={() => {
                handleRestart();
                setShowRestartModal(false);
              }}>
                {t('modals.confirm.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showShareModal && (
        <div className="modal-overlay">
          <div className="modal share-modal">
            <p>{t('modals.share.message')}</p>
            <div className="modal-buttons">
              <button onClick={() => setShowShareModal(false)}>
                {t('modals.share.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showGameOverModal && (
        <div className="modal-overlay">
          <div className="modal">
            <p>{t('modals.gameOver.message')}</p>
            <div className="modal-buttons">
              <button onClick={() => {
                setShowGameOverModal(false);
                handleRestart();
              }}>
                {t('modals.gameOver.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showGameClearModal && (
        <div className="modal-overlay">
          <div className="modal">
            <img 
              src={getVictoryImage(difficulty)} 
              alt="victory" 
              className="victory-image"
            />
            <p>
              {t(`modals.gameClear.${difficulty}.message`)}<br/>
              {isEvening() 
                ? t(`modals.gameClear.${difficulty}.evening`)
                : t(`modals.gameClear.${difficulty}.morning`)}
            </p>
            <div className="modal-buttons">
              {difficulty < 9 ? (
                <>
                  <button onClick={() => {
                    setShowGameClearModal(false);
                    handleRestart();
                  }}>
                    {t('modals.gameClear.confirm')}
                  </button>
                  <button onClick={() => {
                    setDifficulty(difficulty + 1);
                    setLives(difficultySettings[difficulty + 1].lives);
                    setHasUserInput(false);
                    setShowGameClearModal(false);
                  }}>
                    {t('modals.gameClear.nextLevel')}
                  </button>
                </>
              ) : (
                <button onClick={() => {
                  setShowGameClearModal(false);
                  handleRestart();
                }}>
                  {t('modals.gameClear.confirm')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {showSettingsModal && (
        <SettingsModal
          onClose={() => setShowSettingsModal(false)}
          onContact={() => {
            setShowSettingsModal(false);
            setShowContactModal(true);
          }}
          onShare={handleShare}
        />
      )}

      {showContactModal && (
        <div className="modal-overlay">
          <div className="modal">
            <p>{t('modals.contact.message')}</p>
            <div className="modal-buttons">
              <button onClick={() => setShowContactModal(false)}>
                {t('modals.contact.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showGameListModal && (
        <div className="modal-overlay">
          <div className="modal">
            <p>{hasUserInput 
              ? t('modals.confirm.message')
              : t('modals.gameList.message')
            }</p>
            <div className="modal-buttons">
              <button onClick={() => setShowGameListModal(false)}>
                {t('modals.confirm.cancel')}
              </button>
              <button onClick={() => {
                setShowGameListModal(false);
                navigate('/');
              }}>
                {t('modals.confirm.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SudokuGame; 